import { Injectable, inject } from '@angular/core';
import {
  AdministrativeService as LegacyAdministrativeService,
  OneTimePasswordRequestInput as LegacyOneTimePasswordRequestInput
} from '../swagger-api';
import {
  AdministrativeService as AdministrativeService,
  ApplicationService,
  AuthenticationService,
  CompaniesService,
  CompanySecretDTOV1,
  CreateCompanyRequestDTOV1,
  InitResponseV1,
  PatchCompanyRequestDTOV1,
  PatchUserRequestV1,
  UserService
} from '../swagger-api-v1';
import { map, switchMap, tap } from 'rxjs/operators';
import { BehaviorSubject, catchError, Observable, of } from 'rxjs';
import { User } from 'models/user';
import { environment } from '../../../environments/environment';
import { Company, PackageType } from 'models/company';
import { JwtCookieService } from '@cyan/jwt-cookie';
import { Provider } from 'models/provider-config';
import { providerConfig } from '../../../providerconfig';
import { MatDialog } from '@angular/material/dialog';

export const COMPANY_KEY = 'selected_company';

export const UserRole = InitResponseV1.UserRoleEnum;
export type UserRole = InitResponseV1.UserRoleEnum;

@Injectable({
  providedIn: 'root'
})
export class AccountService {
  private legacyAdminService = inject(LegacyAdministrativeService);
  private adminService = inject(AdministrativeService);
  private userService = inject(UserService);
  private applicationService = inject(ApplicationService);
  private companyService = inject(CompaniesService);
  private jwtCookieService = inject(JwtCookieService);
  private authenticationService = inject(AuthenticationService);
  private dialog = inject(MatDialog);

  private _userRole: UserRole;
  private _selectedCompany$ = new BehaviorSubject<Company>(this.getSelectedCompany());

  public getOneTimePassword$(deviceId: number): Observable<string> {
    return this.adminService
      .getOneTimePasswordUsingGET2('login', 3, deviceId)
      .pipe(map((res) => res.password));
  }

  // TODO: Replace this with v1
  public requestOneTimePassword$(deviceId: number): Observable<string> {
    const dto: LegacyOneTimePasswordRequestInput = {
      actionSignature: 'login',
      cyanApplication: 3,
      deviceId,
      initialPassword: true,
      strongPassword: true
    };

    return this.legacyAdminService
      .requestOneTimePasswordUsingPOST(dto)
      .pipe(map((res) => res.password));
  }

  public getUser$(): Observable<User> {
    return this.userService.getUserUsingGET().pipe(map((dto) => User.fromDTO(dto)));
  }

  public loadUserData$(): Observable<void> {
    return this.applicationService.getInitializationUsingGET().pipe(
      tap((response) => (this._userRole = response.userRole)),
      switchMap(() => of())
    );
  }

  public logout(): void {
    this.authenticationService
      .logoutUsingPOST()
      .pipe(
        map((res) => res.logoutUrl),
        catchError(() => of(undefined)),
        tap((logoutUrl) => {
          sessionStorage.removeItem(COMPANY_KEY);
          this.jwtCookieService.invalidateJwtCookie();

          if (logoutUrl) {
            window.location.href = logoutUrl;
          } else {
            this.login();
          }
        })
      )
      .subscribe();
  }

  public login(): void {
    if (providerConfig.provider === Provider.OROM) {
      window.location.href = environment.launcherUrl;
    } else {
      const returnUrl = encodeURIComponent(window.location.href);
      window.location.href = `${environment.launcherUrl}/api/login?returnUrl=${returnUrl}`;
    }
  }

  public getCompanies$(): Observable<Company[]> {
    return this.companyService
      .getCompaniesUsingGET()
      .pipe(map((response) => response.companies.map((dto) => Company.fromDTO(dto))));
  }

  public updateCompany$(company: Company): Observable<Company> {
    const payload = { ...(company.toDTO() as PatchCompanyRequestDTOV1) };
    if (company.packageType === PackageType.LICENSED) {
      payload.licenses = {
        onnet: company.onnetLicenses?.total ?? 0,
        mobile: company.mobileLicenses?.total ?? 0,
        desktop: company.desktopLicenses?.total ?? 0
      };
    }

    return this.companyService
      .updateCompanyUsingPATCH(company.id, payload)
      .pipe(map((dto) => Company.fromDTO(dto)));
  }

  public createCompany$(company: Company, password: string): Observable<Company> {
    const createDTO = { ...company.toDTO(), password } as CreateCompanyRequestDTOV1;
    if (company.packageType === PackageType.LICENSED) {
      createDTO.licenses = {
        onnet: company.onnetLicenses?.total ?? 0,
        mobile: company.mobileLicenses?.total ?? 0,
        desktop: company.desktopLicenses?.total ?? 0
      };
    }
    return this.companyService
      .createCompanyUsingPOST(createDTO)
      .pipe(map((dto) => Company.fromDTO(dto)));
  }

  public deleteCompany$(companyId: number): Observable<void> {
    return this.companyService.deleteCompanyUsingDELETE(companyId);
  }

  public saveSelectedCompany(company: Company): void {
    sessionStorage.setItem(COMPANY_KEY, JSON.stringify(company));
    this._selectedCompany$.next(company);
  }

  public clearSelectedCompany(): void {
    sessionStorage.removeItem(COMPANY_KEY);
    this._selectedCompany$.next(undefined);
  }

  public getSelectedCompany$(): Observable<Company> {
    if (this.userRole !== UserRole.MSP) {
      return of(undefined);
    }

    return this._selectedCompany$.asObservable();
  }

  public changePassword$(password: string): Observable<void> {
    return this.userService.changeUserPasswordUsingPATCH({ newPassword: password });
  }

  private getSelectedCompany(): Company {
    const companyString = sessionStorage.getItem(COMPANY_KEY);

    if (companyString) {
      return JSON.parse(companyString);
    } else {
      return undefined;
    }
  }

  public getCompanySecret$(): Observable<string> {
    return this.companyService.getCompanySecretUsingGET().pipe(
      map((dto: CompanySecretDTOV1) => dto.secret),
      switchMap((secret) => (!secret ? this.regenerateCompanySecret$() : of(secret)))
    );
  }

  public regenerateCompanySecret$(): Observable<string> {
    return this.companyService
      .upsertCompanySecretUsingPOST()
      .pipe(map((response) => response.secret));
  }

  public acceptEula$(): Observable<void> {
    const payload: PatchUserRequestV1 = { eulaAccepted: true };
    return this.userService.patchUserUsingPATCH(payload).pipe(map((): void => void 0));
  }

  public get userRole(): UserRole {
    return this._userRole;
  }
}
